import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";

import { useRootSelector } from "store/hooks";
import {
  activeAccountTypeConfig,
  selectedAccountInfo,
} from "store/account/account-selector";

import { SidePageWrapper } from "../../../layout/sidePageWrapper/SidePageWrapper";
import {
  QuickPayBar,
  QuickPayStep,
} from "components/pages/quickPay/QuickPayBar";
import { TranslationAnchor } from "components/common";

import { Expension } from "../expension/Expension";
import { Gpay } from "components/sliderPage/manageWallet/gpay/Gpay";

import {
  maskString,
  renderConditionalProp,
  typeMapper,
  getCardName,
  getWalletName,
  convert24to12HourTime,
} from "utility";
import { scopes } from "constants/idp-scopes";
import { EWALLET } from "constants/common";

import { useRequestDispatch } from "store/requestHooks";
import { SubmitAndPay, submitAndPay } from "store/payments/payments-action";
import { PaymentActivity } from "store/paymentActivity/paymentActivity-state";

import classes from "./Review.module.scss";
import { Disclaimer } from "components/extra/disclaimer/Disclaimer";
import { useDispatch } from "react-redux";
import { clearAllNotifications } from "store/notification/notification-slice";
import { PaymentMethod } from "store/paymentMethods/paymentMethods-state";

export const Review = () => {
  const { t } = useTranslation([
    "payment_final",
    "quickpay",
    "account_next",
    "payment_next",
    "payment",
  ]);

  const requestDispatch = useRequestDispatch(scopes.users.writeUser);

  const { paymentCenter, client } = useRootSelector((state) => state.config);
  const dispatch = useDispatch();

  const accountInfo = useRootSelector(selectedAccountInfo);
  const { paymentData, meta } = useRootSelector((state) => state.payments);
  const config = useRootSelector(activeAccountTypeConfig);
  const navigate = useNavigate();

  useEffect(() => {
    if (!paymentData) {
      navigate(`/${client.customerKey}/quick-info`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentData]);

  const backButtonHandler = () => {
    dispatch(clearAllNotifications());
    navigate(`/${client.customerKey}/quick-payment`);
  };

  const processPaymentHandler = useCallback(
    (googlePayload?: any, resolve?: any) => {
      requestDispatch<SubmitAndPay, PaymentActivity>(submitAndPay, {
        ...paymentData,
        ...googlePayload,
      })
        .unwrap()
        .then(() => {
          if (resolve) {
            resolve({
              transactionState: "SUCCESS",
            });
          }
          navigate(`/${client.customerKey}/quick-confirmation`, {
            replace: true,
          });
        })
        .catch(() => {
          if (typeof resolve === "function") {
            resolve({
              transactionState: "ERROR",
              error: {
                intent: "PAYMENT_AUTHORIZATION",
                message: t("payment_final:payment_retry"),
                reason: "PAYMENT_DATA_INVALID",
              },
            });
          }
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paymentData],
  );

  const renderEwalletCardNumber = (paymentMethod: PaymentMethod) => {
    if (paymentMethod?.jsonPayload == null) {
      return "";
    }

    const payload = JSON.parse(paymentMethod?.jsonPayload);

    if (payload) {
      // Apple pay display name
      if (!!payload.token?.paymentMethod?.displayName)
        return payload.token.paymentMethod.displayName;
      // Google pay display name
      if (!!payload.description) return payload.description;
    }
    return "";
  };

  return (
    <SidePageWrapper hideDisclaime={true}>
      {paymentData?.isLoading ? <ft-screen-loader></ft-screen-loader> : null}
      <div className={classes.Container}>
        <QuickPayBar
          progress="0.9"
          quickPayType={paymentCenter.quickPayments.quickPayType}
          titles={{
            loan: t("quickpay:loan"),
            information: t("quickpay:information"),
            payment: t("quickpay:payment"),
            review: t("quickpay:review"),
            complete: t("quickpay:complete"),
          }}
          activeStep={QuickPayStep.Review}
        />
        <div className={classes.Review_Container}>
          <Expension />
          <ft-divider class={classes.Divider}></ft-divider>
          <div className={classes.Customer_detail}>
            <p className={classes.heading}>{t("quickpay:customer_detail")}</p>
            <div className={classes.Customer_detail_container}>
              <div className={classes.Detail_items}>
                <p className={classes.Items_head}>
                  {t("account_next:first_name_label")}
                </p>
                <p
                  className={classes.Items_val}
                  data-testid="review-customer-first-name"
                >
                  {paymentData?.firstName}
                </p>
              </div>
              <div className={classes.Detail_items}>
                <p className={classes.Items_head}>
                  {t("account_next:last_name_label")}
                </p>
                <p
                  className={classes.Items_val}
                  data-testid="review-customer-last-name"
                >
                  {paymentData?.lastName}
                </p>
              </div>
              {paymentData?.company ? (
                <div className={classes.Detail_items}>
                  <p className={classes.Items_head}>
                    {t("account_next:company_name_label")}
                  </p>
                  <p className={classes.Items_val}>{paymentData?.company}</p>
                </div>
              ) : null}
              {paymentData?.phone ? (
                <div className={classes.Detail_items}>
                  <p className={classes.Items_head}>
                    {t("account_next:cell_phone")}
                  </p>
                  <p className={classes.Items_val}>{paymentData?.phone}</p>
                </div>
              ) : null}
              {paymentData?.email ? (
                <div className={classes.Detail_items}>
                  <p className={classes.Items_head}>
                    {t("account_next:email_label")}
                  </p>
                  <p
                    className={[classes.Items_val, classes.Email].join(" ")}
                    data-testid="review-customer-email"
                  >
                    {paymentData?.email}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
          <ft-divider class={classes.Divider}></ft-divider>
          <div className={classes.Payment_detail}>
            <p className={classes.heading}>
              {t("payment_final:payment_details")}
            </p>
            <div className={classes.Payment_detail_container}>
              <div className={classes.Detail_items}>
                <p className={classes.Items_head}>
                  {t("payment_final:payment_amount")}
                </p>
                <p className={classes.Items_val}>
                  {t("common:currency_format", {
                    value: paymentData?.paymentAmount
                      ? paymentData?.paymentAmount
                      : accountInfo?.amountDue,
                  })}
                </p>
              </div>
              {paymentData?.feeTotal != null && paymentData?.feeTotal > 0 ? (
                <div className={classes.Detail_items}>
                  <p className={classes.Items_head}>
                    {t("payment_final:convenience_fee_label")}
                  </p>
                  <p className={classes.Items_val}>
                    {" "}
                    {t("common:currency_format", {
                      value: paymentData?.feeTotal,
                    })}
                  </p>
                </div>
              ) : null}
              <div className={classes.Detail_items}>
                <p className={classes.Items_head}>
                  {t("payment_final:total_payment")}
                </p>
                <p className={classes.Items_val}>
                  {t("common:currency_format", {
                    value:
                      parseFloat(
                        paymentData?.paymentAmount?.toString() || "0",
                      ) + (paymentData?.feeTotal || 0),
                  })}
                </p>
              </div>
              {paymentData?.paymentMethod?.bankName ? (
                <div className={classes.Detail_items}>
                  <p className={classes.Items_head}>
                    {t("quickpay:bank_name_label")}
                  </p>
                  <p className={classes.Items_val}>
                    {paymentData?.paymentMethod.bankName}
                  </p>
                </div>
              ) : null}
              {paymentData?.paymentMethod?.bankAccountType ? (
                <div className={classes.Detail_items}>
                  <p className={classes.Items_head}>
                    {t("payment:account_type")}
                  </p>
                  <p className={classes.Items_val}>
                    {typeMapper(
                      paymentData?.paymentMethod.bankAccountType,
                      "accountReverse",
                    )}
                  </p>
                </div>
              ) : null}
              {paymentData?.paymentType ? (
                <div className={classes.Detail_items}>
                  <p className={classes.Items_head}>
                    {t("payment_next:payment_type")}
                  </p>
                  <p className={classes.Items_val}>
                    {
                      config.paymentTypes.find(
                        (pt) => pt.code === paymentData?.paymentType,
                      )?.name
                    }
                  </p>
                </div>
              ) : null}
              {paymentData?.paymentMethod?.paymentMethodType === 2 ? (
                <>
                  <div className={classes.Detail_items}>
                    <p className={classes.Items_head}>
                      {t("common:card_label")}
                    </p>
                    <p
                      className={[classes.Items_val, classes.NonCap].join(" ")}
                    >
                      {paymentData.paymentMethod.cardType &&
                        getCardName(paymentData.paymentMethod.cardType)}
                    </p>
                  </div>
                  <div className={classes.Detail_items}>
                    <p className={classes.Items_head}>
                      {t("payment_next:card_number_label")}
                    </p>
                    <p
                      className={[classes.Items_val, classes.NonCap].join(" ")}
                    >
                      {paymentData?.paymentMethod.lastFour
                        ? paymentData?.paymentMethod.lastFour
                        : ""}
                    </p>
                  </div>
                </>
              ) : paymentData?.paymentMethod?.paymentMethodType === 3 ? (
                <>
                  <div className={classes.Detail_items}>
                    <p className={classes.Items_head}>
                      {t("common:eWallet_label")}
                    </p>
                    <p
                      className={[classes.Items_val, classes.NonCap].join(" ")}
                    >
                      {paymentData.paymentMethod?.walletType &&
                        getWalletName(paymentData.paymentMethod.walletType)}
                    </p>
                  </div>
                  <div className={classes.Detail_items}>
                    <p className={classes.Items_head}>
                      {t("payment_next:card_number_label")}
                    </p>
                    <p
                      className={[classes.Items_val, classes.NonCap].join(" ")}
                    >
                      {renderEwalletCardNumber(paymentData.paymentMethod)}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className={classes.Detail_items}>
                    <p className={classes.Items_head}>
                      {t("payment_next:routing_number_label")} #
                    </p>
                    <p
                      className={[classes.Items_val, classes.NonCap].join(" ")}
                    >
                      {paymentData?.paymentMethod?.routingNumber
                        ? paymentData?.paymentMethod.routingNumber
                        : ""}
                    </p>
                  </div>
                  <div className={classes.Detail_items}>
                    <p className={classes.Items_head}>
                      {t("common:ach_label")}
                    </p>
                    <p
                      className={[classes.Items_val, classes.NonCap].join(" ")}
                    >
                      {paymentData?.paymentMethod?.accountNumber
                        ? maskString(
                            paymentData?.paymentMethod.accountNumber,
                            4,
                          )
                        : ""}
                    </p>
                  </div>
                </>
              )}
              <div className={classes.Detail_items}>
                <p className={classes.Items_head}>{t("common:payment-date")}</p>
                <p className={[classes.Items_val, classes.NonCap].join(" ")}>
                  {t("common:date_format", {
                    value: new Date().toString(),
                  })}
                </p>
              </div>
              {paymentData?.note ? (
                <div
                  className={[
                    classes.Detail_items,
                    classes.Detail_items_fullRow,
                  ].join(" ")}
                >
                  <p className={classes.Items_head}>
                    {t("account_next:note_label")}
                  </p>
                  <p className={[classes.Items_val, classes.NonCap].join(" ")}>
                    {paymentData?.note}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
          <ft-divider class={classes.Divider}></ft-divider>
          <div className={classes.Final_submit}>
            <div className={classes.Disclaimer}>
              <Disclaimer
                translatedText={
                  <Trans
                    i18nKey="quickpay:disclaimer_text"
                    values={{
                      convenienceFee: paymentCenter.defaultFees.convenienceFee,
                    }}
                    components={{ a: <TranslationAnchor /> }}
                  />
                }
                hasBottomDivider={true}
              />
            </div>
            {paymentData?.paymentMethod?.paymentMethodType ? (
              <div className={classes.Final_submit_text}>
                <p>
                  <Trans
                    i18nKey={`quickpay:auth-and-revo_${typeMapper(
                      paymentData.paymentMethod.paymentMethodType,
                      "paymentMethodTypeReverse",
                    )}_auth`}
                    values={{
                      clientName: client.name,
                      contactPhone: client.primaryContactPhone,
                      cutoffTime: convert24to12HourTime(client.cutOffTime!),
                      timeZoneName: typeMapper(
                        client.timeZoneId!,
                        "TimeZoneIdReverse",
                      ),
                    }}
                    components={{ a: <TranslationAnchor /> }}
                  />
                </p>
                <p>
                  <Trans
                    i18nKey={`quickpay:auth-and-revo_${typeMapper(
                      paymentData.paymentMethod.paymentMethodType,
                      "paymentMethodTypeReverse",
                    )}_revo`}
                    values={{
                      todaysDate: t("common:date_format", {
                        value: new Date().toString(),
                      }),
                      contactPhone: client.primaryContactPhone,
                    }}
                    components={{ a: <TranslationAnchor /> }}
                  />
                </p>
              </div>
            ) : (
              <></>
            )}
            <div className={classes.Buttons}>
              <ft-button
                onClick={backButtonHandler}
                class={[classes.Back, "outline-button"].join(" ")}
                label={t("common:back_label")}
                outlined
              ></ft-button>
              {paymentData?.paymentMethodSelectionType === EWALLET &&
              paymentData ? (
                <Gpay
                  paymentData={paymentData}
                  successHandler={processPaymentHandler}
                />
              ) : (
                <ft-button
                  data-testid="submit-and-pay-button"
                  {...renderConditionalProp(
                    meta?.isLoading || false,
                    "disabled",
                  )}
                  class={classes.Make_payment}
                  label={t("payment_final:submit_and_pay")}
                  unelevated
                  onClick={processPaymentHandler}
                ></ft-button>
              )}
            </div>
          </div>
        </div>
      </div>
    </SidePageWrapper>
  );
};
